/**
 * カードの本人認証（3D セキュア）の集計（/money/:mode/three-d-secure）。サーバー: GET /v1/money/three-d-secure（money/threeDSecure.ts）。
 *
 * 何のための画面か: Kumo は全支払いで 3DS を要求している（payments/stripeProvider.ts）が、実際に認証が付くかはカード発行会社次第。
 * 3DS が付いた支払いの「不正」「覚えのない購入」の申し立ては責任がカード発行会社に移る見込みが高く、付かなかった支払いの損失は
 * クリエイターに残る（Kumo は負担しない）。自分のゲームで「3DS なし」の支払いがどれだけあり、そこで申し立てがどれだけ起きているかを見る。
 *
 * **読むだけ**。3DS なしの支払いを断る設定は置かない（founder 判断「保留」）。
 * 見せ方の決め事（moneyLogic.tdsRows）: 3DS あり → なし（理由ごと）→ 不明。「不明」は記録より前（2026-09-27 以前）の支払いで、なしに混ぜない。
 * 責任移転はあくまで推定（Stripe も保証しないと明記）なので、画面では「見込み」と言い、表には出さない（誤って安心させない）。
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Empty, ErrorState, num, PageHeader, Spinner, StatTile } from '../../components/ui';
import { useResource } from '../../hooks';
import { formatPercent, useT, type MessageKey } from '../../i18n';
import { percentFromBps } from '../../money';
import type { ThreeDSecureReport } from '../../moneyApi';
import { disputeTone, recentRange, tdsResultKey, tdsRows, type TdsBucket, type TdsRow } from '../../moneyLogic';
import { useMoney } from './MoneyShell';
import { isPartHidden } from '../../launchHidden';

/**
 * 課金（利用者への販売）を見せない間（2026-09-29 creator/launch-money・launchHidden.ts の payments.threeDSecure）は、
 * 前払いの入金（topups）の分だけを見る: 種類の選択（すべて / アプリ内の購入 / 入金）とゲームごとの切り口を出さない
 * （購入は閉じている = 行はプロジェクトの課金の画面へのリンクで、その画面も隠れている）。文言は Money の持ち物なので触らない
 */
const PURCHASES_HIDDEN = isPartHidden('payments.threeDSecure');

/** 期間の選択肢（日数）。サーバーの既定 = 90 日（申し立て率のシグナルと同じ窓）。最長 366 日なので 1 年まで */
const RANGES = [30, 90, 365] as const;
type GroupBy = 'total' | 'game' | 'month';
type Kind = 'all' | 'purchases' | 'topups';

const rate = (bps: number | null) => (bps === null ? '—' : percentFromBps(bps));

export function ThreeDSecurePage() {
  const { mode, livemode } = useMoney();
  const { t } = useT();
  const [days, setDays] = useState<(typeof RANGES)[number]>(90);
  const [groupBy, setGroupBy] = useState<GroupBy>(PURCHASES_HIDDEN ? 'total' : 'game');
  const [kind, setKind] = useState<Kind>(PURCHASES_HIDDEN ? 'topups' : 'all');
  const range = recentRange(new Date(), days);
  const r = useResource<ThreeDSecureReport>(
    `/v1/money/three-d-secure?livemode=${livemode}&from=${range.from}&to=${range.toExclusive}&groupBy=${groupBy}&kind=${kind}`
  );
  const d = r.data;

  return (
    <>
      <PageHeader eyebrow={t(`money.mode.${mode}`)} title={t('tds.title')} />
      <p className="page-lead muted">{t('tds.lead')}</p>

      <div className="form-inline">
        <label className="field">
          <span>{t('report.range')}</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value) as (typeof RANGES)[number])}>
            {RANGES.map((n) => (
              <option key={n} value={n}>
                {t('report.lastDays', { count: n })}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('report.groupBy')}</span>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
            <option value="total">{t('tds.group.total')}</option>
            {!PURCHASES_HIDDEN && <option value="game">{t('report.group.project')}</option>}
            <option value="month">{t('report.group.month')}</option>
          </select>
        </label>
        {!PURCHASES_HIDDEN && (
          <label className="field">
            <span>{t('tds.kind')}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              <option value="all">{t('tds.kind.all')}</option>
              <option value="purchases">{t('tds.kind.purchases')}</option>
              <option value="topups">{t('tds.kind.topups')}</option>
            </select>
          </label>
        )}
      </div>

      {r.loading && !d && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('tds.title')} onRetry={r.reload} />}
      {d && d.totals.payments === 0 && (
        <section className="card">
          <Empty title={t('tds.emptyTitle')}>
            {/* 販売を閉じている間は「アプリでのカードの支払い」を言わない（2026-09-30 console/english-pass・Money 了承済み）。
                WHY ここで選ぶか: 上の種類の選択は PURCHASES_HIDDEN で消していたが、空の状態の文だけ販売を言ったまま残っていた */}
            <p>{t(PURCHASES_HIDDEN ? 'tds.emptyBodyLaunch' : 'tds.emptyBody')}</p>
          </Empty>
        </section>
      )}
      {d && d.totals.payments > 0 && (
        <>
          <section className="tiles" aria-label={t('tds.title')}>
            <StatTile label={t('tds.payments')} value={num(d.totals.payments)} />
            <StatTile
              label={t('tds.authShare')}
              value={formatPercent(d.totals.authenticated.payments / d.totals.payments, 1)}
              hint={t('tds.authShareHint')}
            />
            <StatTile
              label={t('tds.disputeRate')}
              value={rate(d.totals.disputeRateBps)}
              hint={t('tds.disputeRateHint', { count: d.totals.disputes })}
              tone={disputeTone(d.totals) === 'warn' ? 'warn' : undefined}
            />
            <StatTile
              label={t('tds.unauthDisputeRate')}
              value={rate(d.totals.notAuthenticated.disputeRateBps)}
              hint={t('tds.unauthDisputeRateHint')}
              tone={disputeTone(d.totals.notAuthenticated) === 'warn' ? 'warn' : undefined}
            />
          </section>

          <section className="card">
            <h2 className="card-title">{t('tds.byResult')}</h2>
            <ResultTable rows={tdsRows(d.totals)} />
            <p className="muted small">{t('tds.note')}</p>
          </section>

          {groupBy !== 'total' && d.groups.length > 0 && (
            <section className="card">
              <h2 className="card-title">{groupBy === 'game' ? t('tds.byGame') : t('tds.byMonth')}</h2>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{groupBy === 'game' ? t('report.col.project') : t('report.col.period')}</th>
                      <th className="num">{t('tds.col.payments')}</th>
                      <th className="num">{t('tds.col.authShare')}</th>
                      <th className="num">{t('tds.col.unknown')}</th>
                      <th className="num">{t('tds.col.disputes')}</th>
                      <th className="num">{t('tds.col.rate')}</th>
                      <th className="num">{t('tds.col.unauthRate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.groups.map((g) => (
                      <tr key={g.key}>
                        <td>
                          {groupBy === 'game' ? (
                            g.projectId ? (
                              <Link to={`/p/${g.projectId}/${mode === 'live' ? 'production' : 'development'}/payments`}>{g.name ?? g.projectId.slice(0, 8)}</Link>
                            ) : (
                              <span className="muted">{t('tds.prepaid')}</span>
                            )
                          ) : g.period ? (
                            g.period.slice(0, 7)
                          ) : (
                            g.key
                          )}
                        </td>
                        <td className="num">{num(g.payments)}</td>
                        <td className="num">{g.payments ? formatPercent(g.authenticated.payments / g.payments, 1) : '—'}</td>
                        <td className="num muted">{num(g.unknown.payments)}</td>
                        <td className="num">{num(g.disputes)}</td>
                        <td className="num">
                          <RateCell b={g} />
                        </td>
                        <td className="num">
                          <RateCell b={g.notAuthenticated} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}

function RateCell({ b }: { b: TdsBucket }) {
  const tone = disputeTone(b);
  return <span className={tone === 'warn' ? 'pill pill-warn' : ''}>{rate(b.disputeRateBps)}</span>;
}

function ResultTable({ rows }: { rows: TdsRow[] }) {
  const { t } = useT();
  const label = (r: TdsRow) => {
    if (r.kind !== 'result') {
      if (r.kind === 'authenticated') return <strong>{t('tds.row.authenticated')}</strong>;
      if (r.kind === 'not_authenticated_total') return <strong>{t('tds.row.notAuthenticated')}</strong>;
      return <span className="muted">{t('tds.row.unknown')}</span>;
    }
    const key = tdsResultKey(r.result);
    // 知らない値（Stripe が将来足した result）は生の値を添える
    return <span style={{ paddingLeft: '1rem' }}>{key === 'other' ? `${t('tds.result.other')} (${r.result})` : t(`tds.result.${key}` as MessageKey)}</span>;
  };
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>{t('tds.col.result')}</th>
            <th className="num">{t('tds.col.payments')}</th>
            <th className="num">{t('tds.col.share')}</th>
            <th className="num">{t('tds.col.disputes')}</th>
            <th className="num">{t('tds.col.fraud')}</th>
            <th className="num">{t('tds.col.rate')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.kind === 'result' ? `r:${r.result}` : r.kind}>
              <td>{label(r)}</td>
              <td className="num">{num(r.payments)}</td>
              <td className="num muted">{r.share === null ? '—' : formatPercent(r.share, 1)}</td>
              <td className="num">{num(r.disputes)}</td>
              <td className="num">{num(r.fraudDisputes)}</td>
              <td className="num">
                <RateCell b={r} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
